export const CF_METRO_CODE_HEADER = "cf-metro-code";

export type CtvDmaGroup = "exposed" | "holdout" | "other";

export type DmaInfo = {
  metroCode: string;
  dma: string;
  dmaGroup: CtvDmaGroup;
};

export const CTV_HOLDOUT_DMA_CODES = {
  "789": "Tucson",
  "536": "Youngstown",
  "533": "Hartford-New Haven",
  "545": "Greenville-New Bern",
  "571": "Ft. Myers-Naples",
  "678": "Wichita",
  "548": "West Palm Beach",
  "560": "Raleigh-Durham",
  "825": "San Diego",
  "506": "Boston",
  "563": "Grand Rapids",
  "839": "Las Vegas",
  "770": "Salt Lake City",
  "564": "Charleston-Huntington",
  "751": "Denver",
  "577": "Wilkes Barre-Scranton",
  "531": "Tri-Cities TN-VA",
  "534": "Orlando",
  "575": "Chattanooga",
  "527": "Indianapolis",
  "800": "Bakersfield",
  "573": "Roanoke-Lynchburg",
  "544": "Norfolk",
  "500": "Portland-Auburn",
  "862": "Sacramento",
  "546": "Columbia SC",
  "519": "Charleston SC",
  "543": "Springfield-Holyoke",
  "686": "Mobile-Pensacola",
  "669": "Madison",
  "682": "Davenport-Rock Island-Moline",
  "508": "Pittsburgh",
  "716": "Baton Rouge",
  "529": "Louisville",
  "635": "Austin",
} as const;

export const CTV_EXPOSED_DMA_CODES = {
  "790": "Albuquerque-Santa Fe",
  "547": "Toledo",
  "820": "Portland OR",
  "518": "Greensboro",
  "561": "Jacksonville",
  "752": "Colorado Springs",
  "528": "Miami-Ft. Lauderdale",
  "517": "Charlotte",
  "807": "San Francisco",
  "511": "Washington DC",
  "505": "Detroit",
  "613": "Minneapolis-St. Paul",
  "616": "Kansas City",
  "632": "Paducah",
  "819": "Seattle-Tacoma",
  "566": "Harrisburg",
  "588": "South Bend-Elkhart",
  "539": "Tampa-St. Pete",
  "691": "Huntsville-Decatur",
  "609": "St. Louis",
  "866": "Fresno-Visalia",
  "541": "Lexington",
  "640": "Memphis",
  "521": "Providence",
  "803": "Los Angeles",
  "567": "Greenville-Spartanburg",
  "556": "Richmond-Petersburg",
  "811": "Reno",
  "619": "Springfield MO",
  "617": "Milwaukee",
  "649": "Evansville",
  "504": "Philadelphia",
  "622": "New Orleans",
  "557": "Knoxville",
  "641": "San Antonio",
} as const;

export const CTV_HOLDOUT_DMAS = Object.values(CTV_HOLDOUT_DMA_CODES);
export const CTV_EXPOSED_DMAS = Object.values(CTV_EXPOSED_DMA_CODES);

const DMA_CODE_TO_NAME: Record<string, string> = {
  ...CTV_HOLDOUT_DMA_CODES,
  ...CTV_EXPOSED_DMA_CODES,
};

const emptyDmaInfo = (): DmaInfo => ({
  metroCode: "",
  dma: "",
  dmaGroup: "other",
});

export const normalizeMetroCode = (metroCode?: string | null) =>
  String(metroCode ?? "").trim();

export const getDmaNameFromMetroCode = (metroCode?: string | null) =>
  DMA_CODE_TO_NAME[normalizeMetroCode(metroCode)] ?? "";

export const getCtvDmaGroup = (dma?: string | null): CtvDmaGroup => {
  if (!dma) {
    return "other";
  }

  if ((CTV_EXPOSED_DMAS as readonly string[]).includes(dma)) {
    return "exposed";
  }

  if ((CTV_HOLDOUT_DMAS as readonly string[]).includes(dma)) {
    return "holdout";
  }

  return "other";
};

export const getDmaInfoFromMetroCode = (
  metroCode?: string | null,
): DmaInfo => {
  const code = normalizeMetroCode(metroCode);
  const dma = getDmaNameFromMetroCode(code);

  return {
    metroCode: code,
    dma,
    dmaGroup: getCtvDmaGroup(dma),
  };
};

export const getMetroCodeFromHeaders = (headers: {
  get(name: string): string | null;
}) =>
  normalizeMetroCode(
    headers.get(CF_METRO_CODE_HEADER) || headers.get("CF-Metro-Code"),
  );

export const getDmaInfoFromHeaders = (headers: {
  get(name: string): string | null;
}) => getDmaInfoFromMetroCode(getMetroCodeFromHeaders(headers));

export type CtvFlagKey = "AS202";
export type CtvVariationKey = "control" | "v1" | "v2" | "v3";
export type CtvAssignmentId =
  | "as202control"
  | "as202v1"
  | "as202v2"
  | "as202v3";

export const AS202_BANNER_VARIANTS = ["v1", "v2", "v3"] as const;

export type CtvExperimentAssignment = {
  flagKey: CtvFlagKey;
  variationKey: CtvVariationKey | null;
  assignmentId: CtvAssignmentId | null;
};

const CTV_QA_ASSIGNMENTS: Record<string, CtvExperimentAssignment> = {
  as202control: {
    flagKey: "AS202",
    variationKey: "control",
    assignmentId: "as202control",
  },
  as202v1: {
    flagKey: "AS202",
    variationKey: "v1",
    assignmentId: "as202v1",
  },
  as202v2: {
    flagKey: "AS202",
    variationKey: "v2",
    assignmentId: "as202v2",
  },
  as202v3: {
    flagKey: "AS202",
    variationKey: "v3",
    assignmentId: "as202v3",
  },
};

const AS202_ASSIGNMENT_BY_VARIANT: Record<string, CtvAssignmentId> = {
  control: "as202control",
  v1: "as202v1",
  v2: "as202v2",
  v3: "as202v3",
};

export const isCtvQaUserId = (userId?: string | null) =>
  Boolean(userId && CTV_QA_ASSIGNMENTS[userId]);

export const getCtvExperimentAssignment = (
  qaUserId?: string | null,
): CtvExperimentAssignment => {
  if (qaUserId && CTV_QA_ASSIGNMENTS[qaUserId]) {
    return CTV_QA_ASSIGNMENTS[qaUserId];
  }

  return {
    flagKey: "AS202",
    variationKey: null,
    assignmentId: null,
  };
};

export const resolveCtvAssignmentId = (
  variationKey?: string | null,
): CtvAssignmentId =>
  AS202_ASSIGNMENT_BY_VARIANT[variationKey ?? ""] ?? "as202control";

export const getForcedCtvVariationKey = (
  flagKey: string,
  attributes?: Record<string, unknown> | null,
  enabled = true,
): CtvVariationKey | null => {
  if (!enabled || !attributes) {
    return null;
  }

  const assignment = CTV_QA_ASSIGNMENTS[String(attributes.ctvAssignment ?? "")];

  return assignment?.flagKey === flagKey ? assignment.variationKey : null;
};

export const getCtvDmaAttributes = (info: DmaInfo = emptyDmaInfo()) => {
  const dmaGroup = info.dmaGroup || getCtvDmaGroup(info.dma);

  return {
    dma: info.dma,
    dmaCode: info.metroCode,
    dmaGroup,
    dmaExposed: dmaGroup === "exposed",
    dmaHoldout: dmaGroup === "holdout",
    dmaNonCtv: dmaGroup === "other",
  };
};
